/**
 * The shell's own state: is the sidebar a rail, is the right drawer open and on
 * which tab. Geometry is published as CSS variables on <html> so the content,
 * the footer and the drawer all move from one number.
 *
 * Non-namespaced like every other module here.
 */
const SIDEBAR_KEY = "connect_sidebar_collapsed";

// Below this width the sidebar and the drawer float over the page instead of
// pushing it: a phone has no room to push anything.
const MOBILE_QUERY = "(max-width: 900px)";
const mq = typeof window !== "undefined" && window.matchMedia ? window.matchMedia(MOBILE_QUERY) : null;

const DRAWER_KEY = "pg_drawer_w";
const DRAWER_MIN = 320;
const DRAWER_MAX = 960;
const DRAWER_DEFAULT = 360;
/** The width the person chose by dragging, remembered per browser; the window decides how much of it is shown. */
const chosenWidth = (w) => Math.min(DRAWER_MAX, Math.max(DRAWER_MIN, Math.round(Number(w) || DRAWER_DEFAULT)));
let savedDrawer = DRAWER_DEFAULT;
try { savedDrawer = chosenWidth(localStorage.getItem(DRAWER_KEY) || DRAWER_DEFAULT); } catch (e) { savedDrawer = DRAWER_DEFAULT; }

// The page keeps at least this much room beside the sidebar and the drawer: narrower, its layout breaks (10-21: a
// small laptop with a wide drawer left the page one word wide).
const CONTENT_MIN = 720;
const tokenPx = (name, fallback) => {
  const v = parseFloat(getComputedStyle(document.documentElement).getPropertyValue(name));
  return Number.isFinite(v) ? v : fallback;
};

/**
 * Where the drawer goes in this window and how wide it is shown. Beside the page while the page keeps CONTENT_MIN, and
 * then no wider than that allows; when not even the drawer's narrowest fits beside it, floating over the page. Never
 * wider than 70% of the window. min / max bound dragging its edge; width is the chosen width as shown.
 */
export const drawerFit = (collapsed, chosen) => {
  const room = window.innerWidth - (collapsed ? tokenPx("--rail-width", 64) : tokenPx("--sidebar-width-full", 260)) - CONTENT_MIN;
  const floats = room < DRAWER_MIN;
  const windowMax = Math.max(DRAWER_MIN, Math.round(window.innerWidth * 0.7));
  const max = floats ? windowMax : Math.min(windowMax, room);
  return { floats, min: DRAWER_MIN, max, width: Math.min(chosenWidth(chosen), max) };
};

let watching = false;
/** Publishes the geometry as CSS variables; returns whether the open drawer floats over the page. */
const applyGeometry = (collapsed, drawerOpen, mobile, drawerWidth) => {
  const root = document.documentElement.style;
  const fit = drawerFit(collapsed, drawerWidth || savedDrawer);
  const floats = !!drawerOpen && !mobile && fit.floats;
  root.setProperty("--sidebar-width", mobile ? "0px" : collapsed ? "var(--rail-width)" : "var(--sidebar-width-full)");
  root.setProperty("--drawer-width-open", fit.width + "px");
  root.setProperty("--drawer-width", drawerOpen && !mobile && !floats ? "var(--drawer-width-open)" : "0px");
  document.documentElement.classList.toggle("is-mobile", !!mobile);
  return floats;
};

let saved = false;
try { saved = localStorage.getItem(SIDEBAR_KEY) === "1"; } catch (e) { saved = false; }

const state = () => ({
  sidebarCollapsed: saved,
  mobile: !!(mq && mq.matches),
  mobileMenuOpen: false,
  drawerOpen: false,
  drawerTab: "approvals", // approvals | chat | assistant
  drawerWidth: savedDrawer, // px the person chose by dragging the drawer's left edge, remembered per browser (see drawerFit)
  drawerFloats: false,      // the window is too narrow for the page and the drawer side by side: it floats over the page
});

applyGeometry(saved, false, !!(mq && mq.matches), savedDrawer);

const mutations = {
  SET_SIDEBAR_COLLAPSED(s, v) {
    s.sidebarCollapsed = !!v;
    try { localStorage.setItem(SIDEBAR_KEY, s.sidebarCollapsed ? "1" : "0"); } catch (e) { /* private mode */ }
    s.drawerFloats = applyGeometry(s.sidebarCollapsed, s.drawerOpen, s.mobile, s.drawerWidth);
  },
  SET_MOBILE(s, v) {
    s.mobile = !!v;
    if (!s.mobile) s.mobileMenuOpen = false;
    s.drawerFloats = applyGeometry(s.sidebarCollapsed, s.drawerOpen, s.mobile, s.drawerWidth);
  },
  SET_MOBILE_MENU(s, open) {
    s.mobileMenuOpen = !!open;
    if (s.mobileMenuOpen) s.drawerOpen = false;
    s.drawerFloats = applyGeometry(s.sidebarCollapsed, s.drawerOpen, s.mobile, s.drawerWidth);
  },
  SET_DRAWER(s, { open, tab }) {
    if (typeof open === "boolean") s.drawerOpen = open;
    if (tab) s.drawerTab = tab;
    if (s.drawerOpen && s.mobile) s.mobileMenuOpen = false;
    s.drawerFloats = applyGeometry(s.sidebarCollapsed, s.drawerOpen, s.mobile, s.drawerWidth);
  },
  /** The drawer's width while dragging and after; persist = write it to localStorage (on release). */
  SET_DRAWER_WIDTH(s, { width, persist }) {
    s.drawerWidth = chosenWidth(width);
    if (persist) { try { localStorage.setItem(DRAWER_KEY, String(s.drawerWidth)); } catch (e) { /* private mode */ } }
    s.drawerFloats = applyGeometry(s.sidebarCollapsed, s.drawerOpen, s.mobile, s.drawerWidth);
  },
  /** The window changed size: how wide the drawer is shown, and whether it floats, follow (the chosen width stays). */
  REFLOW_GEOMETRY(s) {
    s.drawerFloats = applyGeometry(s.sidebarCollapsed, s.drawerOpen, s.mobile, s.drawerWidth);
  },
};

const actions = {
  // The ☰ button: on a phone it opens the floating menu, on a desktop it toggles the rail.
  toggleSidebar({ commit, state: s }) {
    if (s.mobile) commit("SET_MOBILE_MENU", !s.mobileMenuOpen);
    else commit("SET_SIDEBAR_COLLAPSED", !s.sidebarCollapsed);
  },
  closeMobileMenu({ commit, state: s }) { if (s.mobileMenuOpen) commit("SET_MOBILE_MENU", false); },
  watchViewport({ commit }) {
    if (!mq || watching) return;   // Dashboard is created again on every sign-in; one listener is enough
    watching = true;
    const on = (e) => commit("SET_MOBILE", e.matches);
    if (mq.addEventListener) mq.addEventListener("change", on); else mq.addListener(on);
    let frame = 0;
    window.addEventListener("resize", () => {
      if (!frame) frame = requestAnimationFrame(() => { frame = 0; commit("REFLOW_GEOMETRY"); });
    });
  },
  openDrawer({ commit }, tab) { commit("SET_DRAWER", { open: true, tab }); },
  closeDrawer({ commit }) { commit("SET_DRAWER", { open: false }); },
  toggleDrawer({ commit, state: s }, tab) {
    const sameTab = !tab || tab === s.drawerTab;
    commit("SET_DRAWER", { open: sameTab ? !s.drawerOpen : true, tab });
  },
};

export default { state, mutations, actions };
